/* eslint-disable react-hooks/exhaustive-deps */
'use client'

import { Button } from '@/components/ui/button'
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { countries } from '@/data/contries'
import { cn } from '@/lib/utils'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

export const LocationSelects = () => {
	const form = useFormContext()
	const [availableRegions, setAvailableRegions] = useState<{ name: string; cities: string[] }[]>([])
	const [availableCities, setAvailableCities] = useState<string[]>([])

	const [openCountry, setOpenCountry] = useState(false)
	const [openRegion, setOpenRegion] = useState(false)
	const [openCity, setOpenCity] = useState(false)

	const [countryFilter, setCountryFilter] = useState('')
	const [regionFilter, setRegionFilter] = useState('')
	const [cityFilter, setCityFilter] = useState('')

	const watchedCountry = form.watch('country')
	const watchedRegion = form.watch('region')

	// Actualizar regiones disponibles cuando cambia el país
	useEffect(() => {
		if (watchedCountry) {
			const country = countries.find((c) => c.code === watchedCountry)
			if (country) {
				setAvailableRegions(country.regions)

				// Solo resetear valores si cambia el país
				const currentRegion = form.getValues('region')
				if (currentRegion && !country.regions.some((r) => r.name === currentRegion)) {
					form.setValue('region', '', { shouldValidate: false })
					form.setValue('city', '', { shouldValidate: false })
				}
			}
		} else {
			setAvailableRegions([])
			form.setValue('region', '', { shouldValidate: false })
			form.setValue('city', '', { shouldValidate: false })
		}
	}, [watchedCountry])

	// Actualizar ciudades disponibles cuando cambia la región
	useEffect(() => {
		if (watchedRegion && watchedCountry) {
			const country = countries.find((c) => c.code === watchedCountry)
			if (country) {
				const region = country.regions.find((r) => r.name === watchedRegion)
				if (region) {
					setAvailableCities(region.cities)

					// Solo resetear ciudad si cambia la región
					const currentCity = form.getValues('city')
					if (currentCity && !region.cities.includes(currentCity)) {
						form.setValue('city', '', { shouldValidate: false })
					}
				}
			}
		} else {
			setAvailableCities([])
			form.setValue('city', '', { shouldValidate: false })
		}
	}, [watchedRegion, watchedCountry])

	// Función para obtener el nombre del país desde su código
	const getCountryName = (code: string) => {
		const country = countries.find((c) => c.code === code)
		return country ? country.name : ''
	}

	// Filtrar países por búsqueda
	const filteredCountries = countries.filter((country) =>
		country.name.toLowerCase().includes(countryFilter.toLowerCase())
	)

	// Filtrar regiones por búsqueda
	const filteredRegions = availableRegions.filter((region) =>
		region.name.toLowerCase().includes(regionFilter.toLowerCase())
	)

	// Filtrar ciudades por búsqueda
	const filteredCities = availableCities.filter((city) =>
		city.toLowerCase().includes(cityFilter.toLowerCase())
	)

	return (
		<div className="flex flex-col gap-4 md:flex-row">
			{/* Combobox para País */}
			<FormField
				control={form.control}
				name="country"
				render={({ field }) => (
					<FormItem className="w-full md:w-1/3">
						<Popover open={openCountry} onOpenChange={setOpenCountry}>
							<PopoverTrigger asChild>
								<FormControl>
									<Button
										variant="outline"
										role="combobox"
										aria-expanded={openCountry}
										className="w-full justify-between"
									>
										{field.value ? getCountryName(field.value) : 'Seleccionar país...'}
										<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
									</Button>
								</FormControl>
							</PopoverTrigger>
							<PopoverContent
								align="start"
								sideOffset={4}
								className="w-[var(--radix-popover-trigger-width)] min-w-[240px] rounded-xl p-0 shadow-lg"
							>
								<div className="flex flex-col">
									<div className="p-2">
										<input
											className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
											placeholder="Buscar país..."
											value={countryFilter}
											onChange={(e) => setCountryFilter(e.target.value)}
										/>
									</div>
									{filteredCountries.length === 0 ? (
										<div className="py-6 text-center text-sm">No se encontró ningún país.</div>
									) : (
										<div className="max-h-64 overflow-auto">
											{filteredCountries.map((country) => (
												<div
													key={country.code}
													className={cn(
														'flex cursor-pointer items-center px-3 py-2 text-sm',
														field.value === country.code
															? 'bg-accent text-accent-foreground'
															: 'hover:bg-muted'
													)}
													onClick={() => {
														form.setValue('country', country.code, { shouldValidate: true })
														// Resetear valores dependientes
														form.setValue('region', '', { shouldValidate: true })
														form.setValue('city', '', { shouldValidate: true })
														setOpenCountry(false)
														setCountryFilter('')
													}}
												>
													<Check
														className={cn(
															'mr-2 h-4 w-4',
															field.value === country.code ? 'opacity-100' : 'opacity-0'
														)}
													/>
													{country.name}
												</div>
											))}
										</div>
									)}
								</div>
							</PopoverContent>
						</Popover>
						<FormMessage />
					</FormItem>
				)}
			/>

			{/* Combobox para Región */}
			<FormField
				control={form.control}
				name="region"
				render={({ field }) => (
					<FormItem className="w-full md:w-1/3">
						<Popover open={openRegion} onOpenChange={setOpenRegion}>
							<PopoverTrigger asChild>
								<FormControl>
									<Button
										variant="outline"
										role="combobox"
										aria-expanded={openRegion}
										className="w-full justify-between"
										disabled={!watchedCountry || availableRegions.length === 0}
									>
										{field.value ? field.value : 'Seleccionar región...'}
										<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
									</Button>
								</FormControl>
							</PopoverTrigger>
							<PopoverContent
								align="start"
								sideOffset={4}
								className="w-[var(--radix-popover-trigger-width)] min-w-[240px] rounded-xl p-0 shadow-lg"
							>
								<div className="flex flex-col">
									<div className="p-2">
										<input
											className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
											placeholder="Buscar región..."
											value={regionFilter}
											onChange={(e) => setRegionFilter(e.target.value)}
											disabled={availableRegions.length === 0}
										/>
									</div>
									{filteredRegions.length === 0 ? (
										<div className="py-6 text-center text-sm">No se encontró ninguna región.</div>
									) : (
										<div className="max-h-64 overflow-auto">
											{filteredRegions.map((region) => (
												<div
													key={region.name}
													className={cn(
														'flex cursor-pointer items-center px-3 py-2 text-sm',
														field.value === region.name
															? 'bg-accent text-accent-foreground'
															: 'hover:bg-muted'
													)}
													onClick={() => {
														form.setValue('region', region.name, { shouldValidate: true })
														// Resetear valor dependiente
														form.setValue('city', '', { shouldValidate: true })
														setOpenRegion(false)
														setRegionFilter('')
													}}
												>
													<Check
														className={cn(
															'mr-2 h-4 w-4',
															field.value === region.name ? 'opacity-100' : 'opacity-0'
														)}
													/>
													{region.name}
												</div>
											))}
										</div>
									)}
								</div>
							</PopoverContent>
						</Popover>
						<FormMessage />
					</FormItem>
				)}
			/>

			{/* Combobox para Ciudad */}
			<FormField
				control={form.control}
				name="city"
				render={({ field }) => (
					<FormItem className="w-full md:w-1/3">
						<Popover open={openCity} onOpenChange={setOpenCity}>
							<PopoverTrigger asChild>
								<FormControl>
									<Button
										variant="outline"
										role="combobox"
										aria-expanded={openCity}
										className="w-full justify-between"
										disabled={!watchedRegion || availableCities.length === 0}
									>
										{field.value ? field.value : 'Seleccionar ciudad...'}
										<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
									</Button>
								</FormControl>
							</PopoverTrigger>
							<PopoverContent
								align="start"
								sideOffset={4}
								className="w-[var(--radix-popover-trigger-width)] min-w-[240px] rounded-xl p-0 shadow-lg"
							>
								<div className="flex flex-col">
									<div className="p-2">
										<input
											className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
											placeholder="Buscar ciudad..."
											value={cityFilter}
											onChange={(e) => setCityFilter(e.target.value)}
											disabled={availableCities.length === 0}
										/>
									</div>
									{filteredCities.length === 0 ? (
										<div className="py-6 text-center text-sm">No se encontró ninguna ciudad.</div>
									) : (
										<div className="max-h-64 overflow-auto">
											{filteredCities.map((city) => (
												<div
													key={city}
													className={cn(
														'flex cursor-pointer items-center px-3 py-2 text-sm',
														field.value === city
															? 'bg-accent text-accent-foreground'
															: 'hover:bg-muted'
													)}
													onClick={() => {
														form.setValue('city', city, { shouldValidate: true })
														setOpenCity(false)
														setCityFilter('')
													}}
												>
													<Check
														className={cn(
															'mr-2 h-4 w-4',
															field.value === city ? 'opacity-100' : 'opacity-0'
														)}
													/>
													{city}
												</div>
											))}
										</div>
									)}
								</div>
							</PopoverContent>
						</Popover>
						<FormMessage />
					</FormItem>
				)}
			/>
		</div>
	)
}
